import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  FiBarChart2, FiShoppingCart, FiShoppingBag, FiTrendingUp, FiPackage,
  FiPrinter, FiSearch, FiAlertTriangle, FiClock, FiDollarSign, FiCreditCard,
} from 'react-icons/fi';
import api from '../services/api';
import { fmtFecha } from '../utils/format';
import '../styles/Reportes.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler,
);

/* ── Helpers ─────────────────────────────────────────────────── */
const S = (n) => `S/ ${(parseFloat(n) || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const N = (n) => {
  const v = parseFloat(n) || 0;
  return v % 1 === 0 ? v.toLocaleString('es-PE') : v.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const iso = (d) => d.toISOString().slice(0, 10);
const hoyISO = () => iso(new Date());

const RANGOS = [
  { key: 'hoy',    label: 'Hoy' },
  { key: 'semana', label: 'Semana' },
  { key: 'mes',    label: 'Mes' },
  { key: 'anio',   label: 'Año' },
];

const rangoFechas = (key) => {
  const h = new Date();
  const hasta = iso(h);
  if (key === 'hoy') return { desde: hasta, hasta };
  if (key === 'semana') {
    const d = new Date(h);
    const dow = (d.getDay() + 6) % 7; // lunes = 0
    d.setDate(d.getDate() - dow);
    return { desde: iso(d), hasta };
  }
  if (key === 'anio') return { desde: `${h.getFullYear()}-01-01`, hasta };
  return { desde: hasta.slice(0, 8) + '01', hasta }; // mes
};

const TABS = [
  { key: 'ventas',         label: 'Ventas',           icon: <FiShoppingCart /> },
  { key: 'compras',        label: 'Compras',          icon: <FiShoppingBag /> },
  { key: 'utilidades',     label: 'Utilidades',       icon: <FiTrendingUp /> },
  { key: 'productos',      label: 'Productos',        icon: <FiPackage /> },
  { key: 'cuentas-cobrar', label: 'Ctas. por Cobrar', icon: <FiDollarSign /> },
  { key: 'cuentas-pagar',  label: 'Ctas. por Pagar',  icon: <FiCreditCard /> },
];

const COLOR_ANTIG = {
  'De 0 a 30 días':   '#16a34a',
  'De 31 a 60 días':  '#f59e0b',
  'De 61 a 90 días':  '#f97316',
  'Más de 90 días':   '#dc2626',
};

const badgeEstadoCta = (v) => {
  const cls = v === 'ANULADO' ? 'rp-badge-bad'
    : (v === 'COBRADO' || v === 'PAGADO') ? 'rp-badge-ok'
    : 'rp-badge-warn';
  return <span className={`rp-badge ${cls}`}>{v}</span>;
};

const PALETA = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];

const chartOpts = (money = true) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { labels: { font: { size: 11 }, boxWidth: 12 } },
    tooltip: money ? { callbacks: { label: (c) => `${c.dataset.label || c.label}: ${S(c.parsed.y ?? c.parsed)}` } } : {},
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 }, color: '#64748b' } },
    y: { grid: { color: '#f1f5f9' }, ticks: { font: { size: 10 }, color: '#64748b' } },
  },
});

const donutOpts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'right', labels: { font: { size: 11 }, boxWidth: 12 } },
    tooltip: { callbacks: { label: (c) => `${c.label}: ${S(c.parsed)}` } },
  },
};

/* ── Componentes UI ──────────────────────────────────────────── */
function Kpi({ label, valor, sub, tone }) {
  return (
    <div className={`rp-kpi ${tone ? `rp-kpi-${tone}` : ''}`}>
      <span className="rp-kpi-label">{label}</span>
      <span className="rp-kpi-valor">{valor}</span>
      {sub && <span className="rp-kpi-sub">{sub}</span>}
    </div>
  );
}

function Tabla({ titulo, icono, cols, rows, vacio = 'Sin datos en el rango seleccionado' }) {
  return (
    <div className="rp-card">
      <div className="rp-card-title">{icono}{titulo}</div>
      <div className="rp-tabla-wrap">
        <table className="rp-tabla">
          <thead>
            <tr>{cols.map((c) => <th key={c.k} className={c.align || ''}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows?.length ? rows.map((r, i) => (
              <tr key={i}>
                {cols.map((c) => (
                  <td key={c.k} className={c.align || ''}>{c.fmt ? c.fmt(r[c.k], r) : (r[c.k] ?? '—')}</td>
                ))}
              </tr>
            )) : (
              <tr><td colSpan={cols.length} className="rp-vacio">{vacio}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Página ──────────────────────────────────────────────────── */
export default function ReportesPage() {
  const [tab, setTab] = useState('ventas');
  const [rangoKey, setRangoKey] = useState('mes');
  const [desde, setDesde] = useState(rangoFechas('mes').desde);
  const [hasta, setHasta] = useState(hoyISO());
  const [almacenes, setAlmacenes] = useState([]);
  const [almacenId, setAlmacenId] = useState('');
  const [diasSinMov, setDiasSinMov] = useState(30);
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [proveedores, setProveedores] = useState([]);
  const [proveedorId, setProveedorId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/api/almacenes')
      .then((r) => setAlmacenes((r.data?.data || r.data || []).filter((a) => a.activo !== 0)))
      .catch(() => {});
    api.get('/api/clientes')
      .then((r) => setClientes(r.data?.data || []))
      .catch(() => {});
    api.get('/api/proveedores')
      .then((r) => setProveedores(r.data?.data || []))
      .catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = { desde, hasta };
      if (tab === 'productos') {
        if (almacenId) params.almacen_id = almacenId;
        params.dias_sin_mov = diasSinMov || 30;
      }
      if (tab === 'cuentas-cobrar' && clienteId) params.cliente_id = clienteId;
      if (tab === 'cuentas-pagar' && proveedorId) params.proveedor_id = proveedorId;
      const r = await api.get(`/api/reportes/${tab}`, { params });
      setData(r.data?.data || null);
    } catch (e) {
      setErr(e.response?.data?.message || 'No se pudo cargar el reporte.');
      setData(null);
    } finally { setLoading(false); }
  }, [tab, desde, hasta, almacenId, diasSinMov, clienteId, proveedorId]);

  useEffect(() => { cargar(); }, [cargar]);

  const aplicarRango = (key) => {
    setRangoKey(key);
    const r = rangoFechas(key);
    setDesde(r.desde); setHasta(r.hasta);
  };

  const imprimir = () => {
    const qs = new URLSearchParams({ desde, hasta });
    if (tab === 'productos') {
      if (almacenId) qs.set('almacen_id', almacenId);
      qs.set('dias_sin_mov', diasSinMov || 30);
    }
    if (tab === 'cuentas-cobrar' && clienteId) qs.set('cliente_id', clienteId);
    if (tab === 'cuentas-pagar' && proveedorId) qs.set('proveedor_id', proveedorId);
    window.open(`/print-reporte/${tab}?${qs.toString()}`, '_blank');
  };

  const rs = data?.resumen || {};
  const margen = parseFloat(rs.ingresos) > 0 ? (parseFloat(rs.utilidad) / parseFloat(rs.ingresos)) * 100 : 0;

  return (
    <div className="rp-page">
      <div className="rp-body">

        {/* ── Sidebar ── */}
        <aside className="rp-sidebar">
          <div className="rp-sidebar-header">
            <span className="rp-sidebar-title"><FiBarChart2 /> Reportes</span>
          </div>
          <ul className="rp-list">
            {TABS.map((t) => (
              <li
                key={t.key}
                className={`rp-item ${tab === t.key ? 'rp-item-sel' : ''}`}
                onClick={() => { setTab(t.key); setData(null); }}
              >
                {t.icon}<span>{t.label}</span>
              </li>
            ))}
          </ul>
        </aside>

        {/* ── Panel principal ── */}
        <main className="rp-main">
          <div className="rp-main-header">
            <div className="rp-main-title">
              {TABS.find((t) => t.key === tab)?.icon} Reporte de {TABS.find((t) => t.key === tab)?.label}
            </div>
            <div className="rp-filtros">
              <div className="rp-chips">
                {RANGOS.map((r) => (
                  <button
                    key={r.key}
                    className={`rp-chip ${rangoKey === r.key ? 'rp-chip-sel' : ''}`}
                    onClick={() => aplicarRango(r.key)}
                  >{r.label}</button>
                ))}
              </div>
              <label className="rp-filtro-lbl">Desde
                <input type="date" value={desde} max={hasta} onChange={(e) => { setDesde(e.target.value); setRangoKey(''); }} />
              </label>
              <label className="rp-filtro-lbl">Hasta
                <input type="date" value={hasta} min={desde} onChange={(e) => { setHasta(e.target.value); setRangoKey(''); }} />
              </label>
              {tab === 'productos' && (
                <>
                  <label className="rp-filtro-lbl">Almacén
                    <select value={almacenId} onChange={(e) => setAlmacenId(e.target.value)}>
                      <option value="">Todos</option>
                      {almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                    </select>
                  </label>
                  <label className="rp-filtro-lbl">Días sin mov.
                    <input
                      type="number" min="1" className="rp-input-dias" value={diasSinMov}
                      onChange={(e) => setDiasSinMov(e.target.value)} />
                  </label>
                </>
              )}
              {tab === 'cuentas-cobrar' && (
                <label className="rp-filtro-lbl">Cliente
                  <select className="rp-select-persona" value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
                    <option value="">Todos</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre_completo || c.nombre || `Cliente #${c.id}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {tab === 'cuentas-pagar' && (
                <label className="rp-filtro-lbl">Proveedor
                  <select className="rp-select-persona" value={proveedorId} onChange={(e) => setProveedorId(e.target.value)}>
                    <option value="">Todos</option>
                    {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </label>
              )}
              <button className="rp-btn-print" onClick={imprimir} disabled={!data}>
                <FiPrinter /> Imprimir
              </button>
            </div>
          </div>

          <div className="rp-content">
            {loading && <div className="rp-msg">Cargando reporte…</div>}
            {err && !loading && <div className="rp-msg rp-msg-err">{err}</div>}

            {!loading && !err && data && tab === 'ventas' && (
              <>
                <div className="rp-kpis">
                  <Kpi label="N° Ventas" valor={N(rs.nro_ventas)} />
                  <Kpi label="Total Vendido" valor={S(rs.total)} tone="ok" />
                  <Kpi label="Contado" valor={S(rs.total_contado)} />
                  <Kpi label="Crédito" valor={S(rs.total_credito)} tone="warn" />
                  <Kpi label="Descuentos" valor={S(rs.descuentos)} />
                  <Kpi label="Anuladas" valor={N(rs.anuladas_nro)} sub={S(rs.anuladas_monto)} tone="bad" />
                </div>

                <div className="rp-graficos">
                  <div className="rp-card rp-chart-lg">
                    <div className="rp-card-title"><FiTrendingUp />Ventas por día</div>
                    <div className="rp-chart-box">
                      <Line
                        data={{
                          labels: (data.por_dia || []).map((d) => fmtFecha(d.fecha)),
                          datasets: [{
                            label: 'Total S/',
                            data: (data.por_dia || []).map((d) => parseFloat(d.total)),
                            borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,0.12)',
                            fill: true, tension: 0.3, pointRadius: 3,
                          }],
                        }}
                        options={chartOpts()}
                      />
                    </div>
                  </div>
                  <div className="rp-card rp-chart-sm">
                    <div className="rp-card-title"><FiBarChart2 />Formas de pago</div>
                    <div className="rp-chart-box">
                      <Doughnut
                        data={{
                          labels: (data.por_forma_pago || []).map((f) => f.forma_pago),
                          datasets: [{
                            data: (data.por_forma_pago || []).map((f) => parseFloat(f.total)),
                            backgroundColor: PALETA,
                          }],
                        }}
                        options={donutOpts}
                      />
                    </div>
                  </div>
                </div>

                <div className="rp-grid-2">
                  <Tabla
                    titulo="Top productos vendidos" icono={<FiPackage />}
                    cols={[
                      { k: 'codigo', label: 'Código' },
                      { k: 'nombre', label: 'Producto' },
                      { k: 'cantidad', label: 'Cant.', align: 'num', fmt: N },
                      { k: 'total', label: 'Total', align: 'num', fmt: S },
                    ]}
                    rows={data.top_productos}
                  />
                  <Tabla
                    titulo="Top clientes" icono={<FiSearch />}
                    cols={[
                      { k: 'cliente', label: 'Cliente' },
                      { k: 'nro', label: 'N°', align: 'num', fmt: N },
                      { k: 'total', label: 'Total', align: 'num', fmt: S },
                    ]}
                    rows={data.top_clientes}
                  />
                  <Tabla
                    titulo="Ventas por vendedor" icono={<FiSearch />}
                    cols={[
                      { k: 'usuario', label: 'Vendedor' },
                      { k: 'nro', label: 'N°', align: 'num', fmt: N },
                      { k: 'total', label: 'Total', align: 'num', fmt: S },
                    ]}
                    rows={data.por_usuario}
                  />
                  <Tabla
                    titulo="Ventas por almacén / documento" icono={<FiBarChart2 />}
                    cols={[
                      { k: 'almacen', label: 'Almacén' },
                      { k: 'nro', label: 'N°', align: 'num', fmt: N },
                      { k: 'total', label: 'Total', align: 'num', fmt: S },
                    ]}
                    rows={data.por_almacen}
                  />
                </div>

                <Tabla
                  titulo={`Detalle de ventas (${data.listado?.length || 0})`} icono={<FiShoppingCart />}
                  cols={[
                    { k: 'numero', label: 'Número' },
                    { k: 'fecha', label: 'Fecha', fmt: fmtFecha },
                    { k: 'cliente', label: 'Cliente' },
                    { k: 'documento', label: 'Doc.' },
                    { k: 'condicion_pago', label: 'Condición' },
                    { k: 'estado', label: 'Estado', fmt: (v) => <span className={`rp-badge ${v === 'ANULADA' ? 'rp-badge-bad' : 'rp-badge-ok'}`}>{v}</span> },
                    { k: 'total', label: 'Total', align: 'num', fmt: S },
                  ]}
                  rows={data.listado}
                />
              </>
            )}

            {!loading && !err && data && tab === 'compras' && (
              <>
                <div className="rp-kpis">
                  <Kpi label="N° Compras" valor={N(rs.nro_compras)} />
                  <Kpi label="Total Comprado" valor={S(rs.total)} tone="ok" />
                  <Kpi label="Compra Promedio" valor={S(rs.compra_promedio)} />
                  <Kpi label="Contado" valor={S(rs.total_contado)} />
                  <Kpi label="Crédito" valor={S(rs.total_credito)} tone="warn" />
                  <Kpi label="Anuladas" valor={N(rs.anuladas_nro)} sub={S(rs.anuladas_monto)} tone="bad" />
                </div>

                <div className="rp-graficos">
                  <div className="rp-card rp-chart-lg">
                    <div className="rp-card-title"><FiTrendingUp />Compras por día</div>
                    <div className="rp-chart-box">
                      <Bar
                        data={{
                          labels: (data.por_dia || []).map((d) => fmtFecha(d.fecha)),
                          datasets: [{
                            label: 'Total S/',
                            data: (data.por_dia || []).map((d) => parseFloat(d.total)),
                            backgroundColor: '#16a34a',
                          }],
                        }}
                        options={chartOpts()}
                      />
                    </div>
                  </div>
                  <div className="rp-card rp-chart-sm">
                    <div className="rp-card-title"><FiBarChart2 />Por proveedor</div>
                    <div className="rp-chart-box">
                      <Doughnut
                        data={{
                          labels: (data.por_proveedor || []).map((p) => p.proveedor),
                          datasets: [{
                            data: (data.por_proveedor || []).map((p) => parseFloat(p.total)),
                            backgroundColor: PALETA,
                          }],
                        }}
                        options={donutOpts}
                      />
                    </div>
                  </div>
                </div>

                <div className="rp-grid-2">
                  <Tabla
                    titulo="Compras por proveedor" icono={<FiShoppingBag />}
                    cols={[
                      { k: 'proveedor', label: 'Proveedor' },
                      { k: 'nro', label: 'N°', align: 'num', fmt: N },
                      { k: 'total', label: 'Total', align: 'num', fmt: S },
                    ]}
                    rows={data.por_proveedor}
                  />
                  <Tabla
                    titulo="Top productos comprados" icono={<FiPackage />}
                    cols={[
                      { k: 'codigo', label: 'Código' },
                      { k: 'nombre', label: 'Producto' },
                      { k: 'cantidad', label: 'Cant.', align: 'num', fmt: N },
                      { k: 'total', label: 'Total', align: 'num', fmt: S },
                    ]}
                    rows={data.top_productos}
                  />
                </div>

                <Tabla
                  titulo={`Detalle de compras (${data.listado?.length || 0})`} icono={<FiShoppingBag />}
                  cols={[
                    { k: 'numero', label: 'Número' },
                    { k: 'fecha', label: 'Fecha', fmt: fmtFecha },
                    { k: 'proveedor', label: 'Proveedor' },
                    { k: 'tipo_documento', label: 'Doc.' },
                    { k: 'condicion_pago', label: 'Condición' },
                    { k: 'estado', label: 'Estado', fmt: (v) => <span className={`rp-badge ${v === 'ANULADA' ? 'rp-badge-bad' : 'rp-badge-ok'}`}>{v}</span> },
                    { k: 'total', label: 'Total', align: 'num', fmt: S },
                  ]}
                  rows={data.listado}
                />
              </>
            )}

            {!loading && !err && data && tab === 'utilidades' && (
              <>
                <div className="rp-kpis">
                  <Kpi label="Ingresos" valor={S(rs.ingresos)} />
                  <Kpi label="Costo" valor={S(rs.costo)} tone="warn" />
                  <Kpi label="Utilidad Bruta" valor={S(rs.utilidad)} tone="ok" />
                  <Kpi label="Margen" valor={`${margen.toFixed(1)}%`} />
                  <Kpi label="Descuentos" valor={S(rs.descuentos)} />
                </div>

                <div className="rp-graficos">
                  <div className="rp-card rp-chart-full">
                    <div className="rp-card-title"><FiTrendingUp />Ingresos vs Costo vs Utilidad por día</div>
                    <div className="rp-chart-box">
                      <Bar
                        data={{
                          labels: (data.por_dia || []).map((d) => fmtFecha(d.fecha)),
                          datasets: [
                            { label: 'Ingresos', data: (data.por_dia || []).map((d) => parseFloat(d.ingresos)), backgroundColor: '#2563eb' },
                            { label: 'Costo', data: (data.por_dia || []).map((d) => parseFloat(d.costo)), backgroundColor: '#f59e0b' },
                            { label: 'Utilidad', data: (data.por_dia || []).map((d) => parseFloat(d.utilidad)), backgroundColor: '#16a34a' },
                          ],
                        }}
                        options={chartOpts()}
                      />
                    </div>
                  </div>
                </div>

                <Tabla
                  titulo="Utilidad por producto" icono={<FiPackage />}
                  cols={[
                    { k: 'codigo', label: 'Código' },
                    { k: 'nombre', label: 'Producto' },
                    { k: 'cantidad', label: 'Cant.', align: 'num', fmt: N },
                    { k: 'ingresos', label: 'Ingresos', align: 'num', fmt: S },
                    { k: 'costo', label: 'Costo', align: 'num', fmt: S },
                    { k: 'utilidad', label: 'Utilidad', align: 'num', fmt: (v) => <b className={parseFloat(v) < 0 ? 'rp-rojo' : 'rp-verde'}>{S(v)}</b> },
                    { k: 'margen', label: 'Margen', align: 'num', fmt: (_, r) => parseFloat(r.ingresos) > 0 ? `${((parseFloat(r.utilidad) / parseFloat(r.ingresos)) * 100).toFixed(1)}%` : '—' },
                  ]}
                  rows={data.por_producto}
                />
                <Tabla
                  titulo="Utilidad por familia" icono={<FiBarChart2 />}
                  cols={[
                    { k: 'familia', label: 'Familia' },
                    { k: 'ingresos', label: 'Ingresos', align: 'num', fmt: S },
                    { k: 'costo', label: 'Costo', align: 'num', fmt: S },
                    { k: 'utilidad', label: 'Utilidad', align: 'num', fmt: (v) => <b className={parseFloat(v) < 0 ? 'rp-rojo' : 'rp-verde'}>{S(v)}</b> },
                  ]}
                  rows={data.por_familia}
                />
              </>
            )}

            {!loading && !err && data && tab === 'productos' && (
              <>
                <div className="rp-kpis">
                  <Kpi label="Productos con stock" valor={N(rs.nro_productos)} />
                  <Kpi label="Unidades" valor={N(rs.unidades)} />
                  <Kpi label="Valor a costo" valor={S(rs.valor_costo)} tone="warn" />
                  <Kpi label="Valor a venta" valor={S(rs.valor_venta)} tone="ok" />
                  <Kpi label="Stock bajo" valor={N(data.stock_bajo?.length)} tone="bad" />
                  <Kpi label="Sin movimiento" valor={N(data.sin_movimiento?.length)} />
                </div>

                <div className="rp-grid-2">
                  <Tabla
                    titulo="Valorización por almacén" icono={<FiBarChart2 />}
                    cols={[
                      { k: 'almacen', label: 'Almacén' },
                      { k: 'nro_productos', label: 'Productos', align: 'num', fmt: N },
                      { k: 'unidades', label: 'Unidades', align: 'num', fmt: N },
                      { k: 'valor_costo', label: 'Valor costo', align: 'num', fmt: S },
                    ]}
                    rows={data.valorizacion_por_almacen}
                  />
                  <Tabla
                    titulo="Top vendidos en el rango" icono={<FiTrendingUp />}
                    cols={[
                      { k: 'codigo', label: 'Código' },
                      { k: 'nombre', label: 'Producto' },
                      { k: 'cantidad', label: 'Cant.', align: 'num', fmt: N },
                      { k: 'total', label: 'Total', align: 'num', fmt: S },
                    ]}
                    rows={data.top_vendidos}
                  />
                </div>

                <Tabla
                  titulo={`Stock bajo mínimo (${data.stock_bajo?.length || 0})`} icono={<FiAlertTriangle />}
                  cols={[
                    { k: 'codigo', label: 'Código' },
                    { k: 'nombre', label: 'Producto' },
                    { k: 'stock_actual', label: 'Stock', align: 'num', fmt: N },
                    { k: 'stock_minimo', label: 'Mínimo', align: 'num', fmt: N },
                    { k: 'faltante', label: 'Faltante', align: 'num', fmt: (v) => <b className="rp-rojo">{N(v)}</b> },
                  ]}
                  rows={data.stock_bajo}
                  vacio="Ningún producto por debajo del stock mínimo"
                />
                <Tabla
                  titulo={`Sin movimiento hace ${diasSinMov || 30}+ días (${data.sin_movimiento?.length || 0})`} icono={<FiClock />}
                  cols={[
                    { k: 'codigo', label: 'Código' },
                    { k: 'nombre', label: 'Producto' },
                    { k: 'stock_actual', label: 'Stock', align: 'num', fmt: N },
                    { k: 'valor_costo', label: 'Valor costo', align: 'num', fmt: S },
                    { k: 'ultima_venta', label: 'Última venta', fmt: (v) => v ? fmtFecha(v) : 'Nunca' },
                  ]}
                  rows={data.sin_movimiento}
                  vacio="Todos los productos con stock registran movimiento reciente"
                />
                <Tabla
                  titulo={`Inventario valorizado (${data.listado?.length || 0})`} icono={<FiPackage />}
                  cols={[
                    { k: 'codigo', label: 'Código' },
                    { k: 'nombre', label: 'Producto' },
                    { k: 'familia', label: 'Familia' },
                    { k: 'stock', label: 'Stock', align: 'num', fmt: N },
                    { k: 'costo_unit', label: 'Costo unit.', align: 'num', fmt: S },
                    { k: 'valor_costo', label: 'Valor total', align: 'num', fmt: S },
                  ]}
                  rows={data.listado}
                />
              </>
            )}

            {!loading && !err && data && tab === 'cuentas-cobrar' && (
              <>
                <div className="rp-kpis">
                  <Kpi label="N° Cuentas" valor={N(rs.nro_cuentas)} />
                  <Kpi label="Total Crédito" valor={S(rs.total)} />
                  <Kpi label="Cobrado" valor={S(rs.cobrado)} tone="ok" />
                  <Kpi label="Saldo Pendiente" valor={S(rs.saldo)} tone="warn" />
                  <Kpi label="Vencido" valor={S(rs.saldo_vencido)} sub={`${N(rs.nro_vencidas)} cuenta(s)`} tone="bad" />
                  <Kpi label="Cobros del periodo" valor={S(rs.cobros_total)} sub={`${N(rs.cobros_nro)} operación(es)`} tone="ok" />
                </div>

                <div className="rp-graficos">
                  <div className="rp-card rp-chart-lg">
                    <div className="rp-card-title"><FiBarChart2 />Saldo por cliente</div>
                    <div className="rp-chart-box">
                      <Bar
                        data={{
                          labels: (data.por_cliente || []).map((c) => c.cliente),
                          datasets: [{
                            label: 'Saldo S/',
                            data: (data.por_cliente || []).map((c) => parseFloat(c.saldo)),
                            backgroundColor: '#f59e0b',
                          }],
                        }}
                        options={chartOpts()}
                      />
                    </div>
                  </div>
                  <div className="rp-card rp-chart-sm">
                    <div className="rp-card-title"><FiClock />Antigüedad de la deuda pendiente (días transcurridos)</div>
                    <div className="rp-chart-box">
                      <Doughnut
                        data={{
                          labels: (data.antiguedad || []).map((a) => a.rango),
                          datasets: [{
                            data: (data.antiguedad || []).map((a) => parseFloat(a.saldo)),
                            backgroundColor: (data.antiguedad || []).map((a) => COLOR_ANTIG[a.rango] || '#64748b'),
                          }],
                        }}
                        options={donutOpts}
                      />
                    </div>
                  </div>
                </div>

                <div className="rp-grid-2">
                  <Tabla
                    titulo="Antigüedad de la deuda pendiente (días transcurridos)" icono={<FiClock />}
                    cols={[
                      { k: 'rango', label: 'Días desde que se generó la deuda' },
                      { k: 'nro', label: 'N° Cuentas', align: 'num', fmt: N },
                      { k: 'saldo', label: 'Saldo', align: 'num', fmt: (v) => <b style={{ color: '#b45309' }}>{S(v)}</b> },
                    ]}
                    rows={data.antiguedad}
                    vacio="Sin deuda pendiente por cobrar"
                  />
                  <Tabla
                    titulo="Saldos por cliente" icono={<FiSearch />}
                    cols={[
                      { k: 'cliente', label: 'Cliente' },
                      { k: 'nro', label: 'N°', align: 'num', fmt: N },
                      { k: 'total', label: 'Crédito', align: 'num', fmt: S },
                      { k: 'cobrado', label: 'Cobrado', align: 'num', fmt: S },
                      { k: 'saldo', label: 'Saldo', align: 'num', fmt: (v) => <b className={parseFloat(v) > 0 ? 'rp-rojo' : 'rp-verde'}>{S(v)}</b> },
                    ]}
                    rows={data.por_cliente}
                  />
                  <Tabla
                    titulo="Por estado" icono={<FiBarChart2 />}
                    cols={[
                      { k: 'estado', label: 'Estado', fmt: badgeEstadoCta },
                      { k: 'nro', label: 'N°', align: 'num', fmt: N },
                      { k: 'total', label: 'Crédito', align: 'num', fmt: S },
                      { k: 'cobrado', label: 'Cobrado', align: 'num', fmt: S },
                      { k: 'saldo', label: 'Saldo', align: 'num', fmt: S },
                    ]}
                    rows={data.por_estado}
                  />
                </div>

                <Tabla
                  titulo={`Detalle de cuentas (${data.listado?.length || 0})`} icono={<FiDollarSign />}
                  cols={[
                    { k: 'venta_numero', label: 'Venta' },
                    { k: 'fecha', label: 'Fecha', fmt: fmtFecha },
                    { k: 'cliente', label: 'Cliente' },
                    { k: 'monto', label: 'Crédito', align: 'num', fmt: S },
                    { k: 'monto_cobrado', label: 'Cobrado', align: 'num', fmt: S },
                    { k: 'saldo', label: 'Saldo', align: 'num', fmt: (v) => <b className={parseFloat(v) > 0 ? 'rp-rojo' : 'rp-verde'}>{S(v)}</b> },
                    { k: 'fecha_vencimiento', label: 'Vence', fmt: (v) => v ? fmtFecha(v) : '—' },
                    { k: 'dias_vencido', label: 'Días venc.', align: 'num', fmt: (v) => parseFloat(v) > 0 ? <b className="rp-rojo">{N(v)}</b> : '—' },
                    { k: 'estado', label: 'Estado', fmt: badgeEstadoCta },
                  ]}
                  rows={data.listado}
                />
                <Tabla
                  titulo={`Cobros del periodo (${data.cobros?.length || 0})`} icono={<FiTrendingUp />}
                  cols={[
                    { k: 'fecha', label: 'Fecha', fmt: fmtFecha },
                    { k: 'venta_numero', label: 'Venta' },
                    { k: 'cliente', label: 'Cliente' },
                    { k: 'forma_pago', label: 'Forma de pago' },
                    { k: 'referencia', label: 'Referencia' },
                    { k: 'usuario', label: 'Usuario' },
                    { k: 'monto', label: 'Monto', align: 'num', fmt: S },
                  ]}
                  rows={data.cobros}
                  vacio="Sin cobros registrados en el rango"
                />
              </>
            )}

            {!loading && !err && data && tab === 'cuentas-pagar' && (
              <>
                <div className="rp-kpis">
                  <Kpi label="N° Cuentas" valor={N(rs.nro_cuentas)} />
                  <Kpi label="Total Crédito" valor={S(rs.total)} />
                  <Kpi label="Pagado" valor={S(rs.pagado)} tone="ok" />
                  <Kpi label="Saldo por Pagar" valor={S(rs.saldo)} tone="warn" />
                  <Kpi label="Pagos del periodo" valor={S(rs.pagos_total)} sub={`${N(rs.pagos_nro)} operación(es)`} tone="ok" />
                </div>

                <div className="rp-graficos">
                  <div className="rp-card rp-chart-lg">
                    <div className="rp-card-title"><FiBarChart2 />Saldo por proveedor</div>
                    <div className="rp-chart-box">
                      <Bar
                        data={{
                          labels: (data.por_proveedor || []).map((p) => p.proveedor),
                          datasets: [{
                            label: 'Saldo S/',
                            data: (data.por_proveedor || []).map((p) => parseFloat(p.saldo)),
                            backgroundColor: '#7c3aed',
                          }],
                        }}
                        options={chartOpts()}
                      />
                    </div>
                  </div>
                </div>

                <div className="rp-grid-2">
                  <Tabla
                    titulo="Saldos por proveedor" icono={<FiShoppingBag />}
                    cols={[
                      { k: 'proveedor', label: 'Proveedor' },
                      { k: 'nro', label: 'N°', align: 'num', fmt: N },
                      { k: 'total', label: 'Crédito', align: 'num', fmt: S },
                      { k: 'pagado', label: 'Pagado', align: 'num', fmt: S },
                      { k: 'saldo', label: 'Saldo', align: 'num', fmt: (v) => <b className={parseFloat(v) > 0 ? 'rp-rojo' : 'rp-verde'}>{S(v)}</b> },
                    ]}
                    rows={data.por_proveedor}
                  />
                  <Tabla
                    titulo="Por estado" icono={<FiBarChart2 />}
                    cols={[
                      { k: 'estado', label: 'Estado', fmt: badgeEstadoCta },
                      { k: 'nro', label: 'N°', align: 'num', fmt: N },
                      { k: 'total', label: 'Crédito', align: 'num', fmt: S },
                      { k: 'pagado', label: 'Pagado', align: 'num', fmt: S },
                      { k: 'saldo', label: 'Saldo', align: 'num', fmt: S },
                    ]}
                    rows={data.por_estado}
                  />
                </div>

                <Tabla
                  titulo={`Detalle de cuentas (${data.listado?.length || 0})`} icono={<FiCreditCard />}
                  cols={[
                    { k: 'compra_numero', label: 'Compra' },
                    { k: 'fecha', label: 'Fecha', fmt: fmtFecha },
                    { k: 'proveedor', label: 'Proveedor' },
                    { k: 'monto', label: 'Crédito', align: 'num', fmt: S },
                    { k: 'monto_pagado', label: 'Pagado', align: 'num', fmt: S },
                    { k: 'saldo', label: 'Saldo', align: 'num', fmt: (v) => <b className={parseFloat(v) > 0 ? 'rp-rojo' : 'rp-verde'}>{S(v)}</b> },
                    { k: 'estado', label: 'Estado', fmt: badgeEstadoCta },
                  ]}
                  rows={data.listado}
                />
                <Tabla
                  titulo={`Pagos del periodo (${data.pagos?.length || 0})`} icono={<FiTrendingUp />}
                  cols={[
                    { k: 'fecha', label: 'Fecha', fmt: fmtFecha },
                    { k: 'compra_numero', label: 'Compra' },
                    { k: 'proveedor', label: 'Proveedor' },
                    { k: 'forma_pago', label: 'Forma de pago' },
                    { k: 'referencia', label: 'Referencia' },
                    { k: 'usuario', label: 'Usuario' },
                    { k: 'monto', label: 'Monto', align: 'num', fmt: S },
                  ]}
                  rows={data.pagos}
                  vacio="Sin pagos registrados en el rango"
                />
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
